'use client'

import { useEffect, useSyncExternalStore } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { formatPrice, isCustomerAuthActive, getCheckoutPath } from "@/utils/util"
import { Plus, ShoppingBag } from "lucide-react"
import CartCard from "./CartCard"
import { navigateUrl } from "@/utils/client-request"
import Skeleton from "@/components/elements/Skeleton"
import { useCart, useCartRecommendations } from "@/lib/commerce/hooks"
import { toast } from "@/lib/toast"
import { useSession } from "next-auth/react"
import ProductCard, { CARD_ASPECT } from "../product/ProductCard"
import EmptyState from "../elements/EmptyState"
import { BuyXGetYOffers, OrderRewardOffers } from "./RewardOffers"
import ProgressNote from "../elements/ProgressNote"
import { panelFocusRing } from "../elements/SidePanel"

const gridClass = 'grid grid-cols-2 lg:grid-cols-4 gap-x-1 gap-y-8 lg:gap-y-12'
const subscribeNoop = () => () => {}

export default function Cart({ vendor, wishlist }: { vendor: any, wishlist?: any }) {
  const pathname = usePathname()
  const { data: session } = useSession()
  const { cart, initialLoading, isValidating, canCheckout, validateCart, freeShippingProgress, amount, sale, activeOrderDiscount, orderDiscountAmount, appliedCoupon, couponDiscount, cartTotal, loyaltyDiscountAmount, loyaltySaleOffsetAmount } = useCart()
  const validCoupon = couponDiscount > 0 && appliedCoupon && couponDiscount <= cartTotal ? couponDiscount : 0
  const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, Math.max(0, cartTotal - orderDiscountAmount - validCoupon)) : 0
  const hasMounted = useSyncExternalStore(subscribeNoop, () => true, () => false)
  const searchParams = useSearchParams()

  const products = cart?.products || []
  const isLoggedIn = !!session?.user?.id
  const checkoutHref = navigateUrl(getCheckoutPath(vendor, isLoggedIn), pathname)
  const { recommendedProducts, isLoading: isLoadingRecommendations } = useCartRecommendations(vendor, products, 4)

  useEffect(() => {
    if (searchParams.has('error')) toast.error('Бүртгэл олдсонгүй')
  }, [searchParams])

  useEffect(() => {
    if (products.length > 0 && vendor) validateCart()
  }, [products.length, vendor])

  if (!hasMounted) return null

  const showWishlist = isCustomerAuthActive(vendor) && isLoggedIn && wishlist?.products?.length > 0
  const extraTitle = showWishlist ? 'Хүслийн жагсаалт' : 'Санал болгох бүтээгдэхүүн'
  const extraProducts = showWishlist ? wishlist.products : recommendedProducts
  const orderDiscountNote = (() => {
    if (!(activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0)) return null
    const subPrice = amount - sale
    const minAmt = activeOrderDiscount.minOrderAmount
    const target = activeOrderDiscount.targets?.find((t: any) => t.role === 'target' && t.targetId === null)
    if (!target) return null
    const discountLabel = target.valueType === 'percent' ? `${target.valuePercent}%-ийн хөнгөлөлт` : target.valueType === 'amount' ? `${formatPrice(target.valueAmount)} хөнгөлөлт` : null
    if (!discountLabel) return null
    return <ProgressNote label={`${formatPrice(minAmt)}-с дээш захиалгад ${discountLabel}`} value={subPrice >= minAmt ? `-${formatPrice(orderDiscountAmount)}` : `${formatPrice(minAmt - subPrice)} дутуу`} pct={(subPrice / minAmt) * 100} />
  })()

  return (
    <div className="w-full px-4 lg:px-6 pt-8 lg:pt-12 pb-20">
      <div className="flex items-end justify-between gap-4 pb-6 border-b border-border">
        <h1 className="text-3xl sm:text-4xl tracking-tight text-foreground">Сагс</h1>
        {!initialLoading && products.length > 0 && <span className="text-[13px] text-muted-foreground tabular-nums">{products.length} бүтээгдэхүүн</span>}
      </div>

      {initialLoading ? (
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-16">
          <div className="divide-y divide-border">
            {[0, 1].map(i => (
              <div key={i} className="flex gap-6 py-8">
                <Skeleton className="w-24 sm:w-32 aspect-3/4 rounded-none" />
                <div className="flex-1 space-y-2 pt-1">
                  <Skeleton className="h-4 w-2/3 rounded-none" />
                  <Skeleton className="h-3.5 w-1/3 rounded-none" />
                </div>
              </div>
            ))}
          </div>
          <div className="pt-8 space-y-4">
            <Skeleton className="h-4 w-full rounded-none" />
            <Skeleton className="h-8 w-full rounded-none" />
            <Skeleton className="h-12 w-full rounded-none" />
          </div>
        </div>
      ) : products.length > 0 ? (
        <>
          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-16">
            <div className="min-w-0">
              <ul className="divide-y divide-border">
                {products.map((product: any, index: number) => (
                  <li key={`${product.isRewardItem ? 'reward-' : ''}${product.id}-${product.variant?.id}-${index}`} className="py-8">
                    <CartCard product={product} />
                  </li>
                ))}
              </ul>

              <BuyXGetYOffers className="mt-4 mb-8 sm:p-6" />
              <OrderRewardOffers className="mt-4 mb-8 sm:p-6" />
            </div>

            <aside className="mt-8 lg:mt-0 pt-8 border-t border-border lg:border-t-0 lg:sticky lg:top-24 lg:self-start space-y-6">
              {freeShippingProgress && !products.every((p: any) => p.isDigital === true) && (
                <ProgressNote label="Үнэгүй хүргэлт" value={freeShippingProgress.reached ? 'Хангагдсан' : `${formatPrice(freeShippingProgress.remaining)} дутуу`} pct={freeShippingProgress.percentage} />
              )}
              {orderDiscountNote}
              {vendor?.orderMinAmount > 0 && (amount - sale) < vendor.orderMinAmount && (
                <ProgressNote label="Захиалгын доод дүн" value={`${formatPrice(vendor.orderMinAmount - (amount - sale))} дутуу`} pct={((amount - sale) / vendor.orderMinAmount) * 100} />
              )}

              <dl className="space-y-3 text-sm">
                {loyaltyDiscount > 0 && (
                  <div className="flex items-baseline justify-between gap-4 text-muted-foreground">
                    <dt>Лояалти хөнгөлөлт</dt>
                    <dd className="tabular-nums">-{formatPrice(loyaltyDiscount + loyaltySaleOffsetAmount)}</dd>
                  </div>
                )}
                <div className="flex items-baseline justify-between gap-4 text-lg">
                  <dt>Нийт дүн</dt>
                  <dd className="tabular-nums">{formatPrice(Math.max(0, cartTotal - orderDiscountAmount - validCoupon - loyaltyDiscount))}</dd>
                </div>
              </dl>

              <div>
                <button type="button" onClick={() => { window.location.href = checkoutHref }} disabled={isValidating || !canCheckout()} className={`w-full h-12 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${panelFocusRing}`}>
                  Төлбөр төлөх
                </button>
                <p className="mt-3 text-xs text-muted-foreground">Төлбөр төлөх үед хүргэлтийн төлбөр тооцогдоно.</p>
              </div>

              {cart?.products?.some((product: any) => product.quantity === 0 || product.isOutOfStock) && (
                <div className="border-l border-foreground pl-4">
                  <p className="text-[13px]">Үлдэгдэл дууссан бараа</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Таны сагсанд үлдэгдэл хүрэлцэхгүй бараа байна. Эдгээр барааг устгаад захиалга өгнө үү.</p>
                </div>
              )}

              {(vendor?.terms?.delivery || vendor?.terms?.payment) && (
                <div className="border-t border-border">
                  {[['Хүргэлтийн нөхцөл', vendor?.terms?.delivery], ['Төлбөрийн нөхцөл', vendor?.terms?.payment]].filter(([, html]) => html).map(([title, html]) => (
                    <details key={title} className="group border-b border-border">
                      <summary className={`flex items-center justify-between h-12 cursor-pointer list-none text-[13px] [&::-webkit-details-marker]:hidden ${panelFocusRing}`}>
                        {title}
                        <Plus aria-hidden className="size-3.5 transition-transform duration-150 group-open:rotate-45" strokeWidth={1.5} />
                      </summary>
                      <div className="pb-5 prose prose-sm max-w-none text-[13px] leading-relaxed text-muted-foreground" dangerouslySetInnerHTML={{ __html: html }} />
                    </details>
                  ))}
                </div>
              )}
            </aside>
          </div>

          {(showWishlist || isLoadingRecommendations || recommendedProducts.length > 0) && (
            <section className="mt-20 lg:mt-28">
              <h2 className="mb-6 lg:mb-8 text-2xl sm:text-4xl tracking-tight text-foreground">{extraTitle}</h2>
              {!showWishlist && isLoadingRecommendations ? (
                <div className={gridClass}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i}>
                      <Skeleton className={`w-full ${CARD_ASPECT} rounded-none`} />
                      <Skeleton className="h-3.5 w-3/4 mt-3 rounded-none" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className={gridClass}>
                  {extraProducts.map((product: any, index: number) => (
                    <ProductCard key={`${product.id}-${product.variant?.id || index}`} product={product} vendor={vendor} showVariantName={showWishlist} />
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      ) : (
        <EmptyState icon={ShoppingBag} title="Таны сагс хоосон байна" description="Та яг одоо сагсандаа бараа нэмээрэй!">
          <a href={navigateUrl('/product', pathname)} className={`inline-flex items-center h-11 px-8 border border-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background ${panelFocusRing}`}>Дэлгүүр хэсэх</a>
        </EmptyState>
      )}
    </div>
  )
}
